import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatCurrency, formatDate } from "./api";
import { trendText } from "../components/executive/TrendCell";
import { drawPdfBrand } from "./brand";

const NAVY = [29, 78, 216];
const SLATE_900 = [15, 23, 42];
const SLATE_500 = [100, 116, 139];
const SLATE_200 = [226, 232, 240];
const ROSE = [244, 63, 94];
const EMERALD = [16, 185, 129];
const AMBER = [245, 158, 11];
const WHITE = [255, 255, 255];
const M = 36;
const p_worse = (t) => /risk \+|later/.test(t);

function header(doc, pageW, filterText) {
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageW, 56, "F");
  doc.setTextColor(...WHITE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Compass PM", M, 28);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("by Uplift Communications Corporation", M, 44);
  doc.text(`Executive briefing · ${new Date().toLocaleString()}`, pageW - M, 32, { align: "right" });
  drawPdfBrand(doc, pageW, M, `Executive briefing · ${new Date().toLocaleString()}`);
  doc.setTextColor(...SLATE_900);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("Executive overview", M, 86);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...SLATE_500);
  doc.text(filterText, M, 104);
}

function cards(doc, pageW, c, y) {
  const items = [
    ["Portfolios", c.portfolio_count], ["Programs", c.program_count],
    ["Projects", c.project_count, `${c.open_project_count} open`], ["Project managers", c.pm_count],
  ];
  const w = (pageW - M * 2 - 24) / 4;
  items.forEach(([l, v, sub], i) => {
    const x = M + i * (w + 8);
    doc.setDrawColor(...SLATE_200);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(x, y, w, 58, 6, 6, "FD");
    doc.setTextColor(...SLATE_500); doc.setFontSize(8); doc.setFont("helvetica", "bold");
    doc.text(l.toUpperCase(), x + 10, y + 16);
    doc.setTextColor(...SLATE_900); doc.setFontSize(18);
    doc.text(String(v), x + 10, y + 40);
    if (sub) { doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(...SLATE_500); doc.text(sub, x + 10, y + 52); }
  });
  return y + 76;
}

const atRiskReason = (p) => [
  (p.days_late || 0) > 0 && `${p.days_late}d late`,
  p.stated_budget > 0 && p.estimated_cost > p.stated_budget && `${formatCurrency(p.estimated_cost - p.stated_budget)} over budget`,
  p.risk_tier === "high" && "high risk score",
].filter(Boolean).join(", ");

function callout(doc, pageW, projects, y) {
  const risky = projects.filter((p) => p.is_open && p.health === "at_risk");
  const lines = risky.length
    ? risky.map((p) => `• ${p.name}${p.pm_name ? ` (${p.pm_name})` : ""}: ${atRiskReason(p)}`)
    : ["No open projects are late, over budget or high risk."];
  const wrapped = doc.splitTextToSize(lines.join("\n"), pageW - M * 2 - 20);
  const h = 26 + wrapped.length * 12;
  doc.setFillColor(...(risky.length ? [255, 241, 242] : [236, 253, 245]));
  doc.setDrawColor(...(risky.length ? ROSE : EMERALD));
  doc.roundedRect(M, y, pageW - M * 2, h, 6, 6, "FD");
  doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...(risky.length ? ROSE : EMERALD));
  doc.text(risky.length ? `Needs attention (${risky.length})` : "All open projects on track", M + 10, y + 16);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...SLATE_900);
  doc.text(wrapped, M + 10, y + 30);
  return y + h + 18;
}

function projectsTable(doc, projects, y) {
  autoTable(doc, {
    startY: y,
    head: [["Project", "Program", "PM", "Owner", "Due", "Calc delivery", "Est cost", "Budget", "Progress", "Risk", "vs last week", "State"]],
    body: projects.map((p) => [
      p.name, p.program_name || "—", p.pm_name || "Unassigned", p.project_owner_name || "Unassigned",
      p.stated_due_date ? formatDate(p.stated_due_date) : "—",
      p.planned_delivery ? `${formatDate(p.planned_delivery)}${p.days_late != null ? ` (${p.days_late > 0 ? "+" : ""}${p.days_late}d)` : ""}` : "—",
      formatCurrency(p.estimated_cost), p.stated_budget ? formatCurrency(p.stated_budget) : "—",
      `${p.percent_complete}%`, p.risk_tier, trendText(p).replace(/→/g, "->"), !p.is_open ? "Completed" : p.health === "at_risk" ? "At risk" : "On track",
    ]),
    styles: { fontSize: 8, cellPadding: 4, textColor: SLATE_900 },
    headStyles: { fillColor: NAVY, textColor: WHITE, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: { 6: { halign: "right" }, 7: { halign: "right" }, 8: { halign: "right" } },
    didParseCell: (d) => {
      if (d.section !== "body") return;
      const t = String(d.cell.raw || "").toLowerCase();
      if (d.column.index === 9) d.cell.styles.textColor = t === "high" ? ROSE : t === "medium" ? AMBER : EMERALD;
      if (d.column.index === 10 && p_worse(t)) d.cell.styles.textColor = ROSE;
      if (d.column.index === 11) d.cell.styles.textColor = t === "at risk" ? ROSE : t === "on track" ? EMERALD : SLATE_500;
      if (d.column.index === 5 && t.includes("(+")) d.cell.styles.textColor = ROSE;
    },
  });
  return doc.lastAutoTable.finalY + 20;
}

function perPmTable(doc, perPm, y) {
  if (y > doc.internal.pageSize.getHeight() - 120) { doc.addPage(); y = 50; }
  doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(...SLATE_900);
  doc.text("Projects per PM", M, y);
  autoTable(doc, {
    startY: y + 6, tableWidth: 360,
    head: [["Project manager", "Projects", "Open"]],
    body: perPm.map((r) => [r.pm_name, r.project_count, r.open_count]),
    styles: { fontSize: 9, cellPadding: 4, textColor: SLATE_900 },
    headStyles: { fillColor: NAVY, textColor: WHITE, fontStyle: "bold" },
    columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
  });
}

export function exportExecutivePDF({ counts, projects, perPm, filterText }) {
  const doc = new jsPDF({ unit: "pt", format: "letter", orientation: "landscape" });
  const pageW = doc.internal.pageSize.getWidth();
  header(doc, pageW, filterText);
  let y = cards(doc, pageW, counts, 118);
  y = callout(doc, pageW, projects, y);
  y = projectsTable(doc, projects, y);
  perPmTable(doc, perPm, y);
  const n = doc.internal.getNumberOfPages();
  for (let i = 1; i <= n; i++) {
    doc.setPage(i); doc.setFontSize(8); doc.setTextColor(...SLATE_500);
    doc.text(`Compass PM executive briefing · Page ${i} of ${n}`, M, doc.internal.pageSize.getHeight() - 20);
  }
  doc.save(`Compass_PM_Executive_Briefing_${new Date().toISOString().slice(0, 10)}.pdf`);
}
